import { useEffect, useRef } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { type RefetchFnDynamic, useRelayEnvironment, fetchQuery } from 'react-relay';
import type { ConcreteRequest, OperationType } from 'relay-runtime';
import { useGetCurrentUrl, useGetCurrentUrlPath } from './useGetCurrentUrl.tsx';

type OperationWithCloudIdAndCurrentURL = Omit<OperationType, 'variables'> & {
	readonly variables: {
		cloudId: string;
		currentURL: string;
	};
};

type UseRefetchProjectsParams<
	RefetchContentKey extends Readonly<{ ' $data'?: unknown; ' $fragmentSpreads': unknown }>,
	QueryVariables,
> = {
	cloudId: string;
	isSelectedPath(menuId: string): boolean;
	menuId: string;
	refetch: RefetchFnDynamic<OperationWithCloudIdAndCurrentURL, RefetchContentKey>;
	QUERY: ConcreteRequest;
	queryVariables?: Partial<QueryVariables>;
};

export function useRefetchWhenIsSelected<
	K extends Readonly<{ ' $data'?: unknown; ' $fragmentSpreads': unknown }>,
	Q,
>(params: UseRefetchProjectsParams<K, Q>): void {
	const { cloudId, isSelectedPath, menuId, refetch, queryVariables, QUERY } = params;
	const environment = useRelayEnvironment();
	const currentURL = useGetCurrentUrl();
	const currentURLPath = useGetCurrentUrlPath();
	const isInitialEffect = useRef(true);

	useEffect(() => {
		// The first render is usually hydrated from server-preloaded data.
		// Skipping the initial run avoids an immediate duplicate network request on page load.
		if (isInitialEffect.current && fg('nike_skip_initial_nav4projectscontentquery_refetch')) {
			isInitialEffect.current = false;
			return;
		}

		if (isSelectedPath(menuId)) {
			fetchQuery(environment, QUERY, {
				cloudId,
				currentURL,
				currentURLPath,
				...queryVariables,
			}).subscribe({
				complete: () => {
					refetch(
						{ cloudId, currentURL, ...queryVariables },
						{
							fetchPolicy: 'store-only',
						},
					);
				},
			});
		}
	}, [
		cloudId,
		isSelectedPath,
		menuId,
		refetch,
		currentURL,
		currentURLPath,
		environment,
		queryVariables,
		QUERY,
	]);
}
